/**
 * Phone numbers in E.164 (`+` and up to 15 digits), the only form Hire
 * stores. Validation is structural (country code plus a plausible length),
 * not a full numbering-plan check: the sign-in code sent to the number is
 * the real proof that it works.
 */

/** A country and its calling code, for the PhoneField's country list. */
export interface CallingCode {
  /** ISO 3166-1 alpha-2 region, for the country's name in the current language. */
  region: string;
  /** The calling code without `+`. */
  code: string;
  /** Digits in a national number (without the trunk 0): minimum and maximum. */
  digits: [number, number];
}

/**
 * The countries Hire's schools and candidates mostly come from, China and
 * the main English-speaking countries first. Others can type a full
 * international number starting with `+`. China is the default.
 */
export const DEFAULT_CALLING_CODE: CallingCode = { region: 'CN', code: '86', digits: [11, 11] };

/** The country list, China first (see `DEFAULT_CALLING_CODE`). */
export const CALLING_CODES: readonly CallingCode[] = [
  DEFAULT_CALLING_CODE,
  { region: 'HK', code: '852', digits: [8, 8] },
  { region: 'MO', code: '853', digits: [8, 8] },
  { region: 'TW', code: '886', digits: [9, 9] },
  { region: 'GB', code: '44', digits: [10, 10] },
  { region: 'IE', code: '353', digits: [7, 9] },
  { region: 'US', code: '1', digits: [10, 10] },
  { region: 'CA', code: '1', digits: [10, 10] },
  { region: 'AU', code: '61', digits: [9, 9] },
  { region: 'NZ', code: '64', digits: [8, 10] },
  { region: 'ZA', code: '27', digits: [9, 9] },
  { region: 'SG', code: '65', digits: [8, 8] },
  { region: 'MY', code: '60', digits: [9, 10] },
  { region: 'TH', code: '66', digits: [8, 9] },
  { region: 'VN', code: '84', digits: [9, 10] },
  { region: 'PH', code: '63', digits: [10, 10] },
  { region: 'JP', code: '81', digits: [9, 10] },
  { region: 'KR', code: '82', digits: [9, 10] },
  { region: 'IN', code: '91', digits: [10, 10] },
  { region: 'AE', code: '971', digits: [8, 9] },
  { region: 'FR', code: '33', digits: [9, 9] },
  { region: 'DE', code: '49', digits: [6, 13] },
  { region: 'ES', code: '34', digits: [9, 9] },
  { region: 'IT', code: '39', digits: [6, 11] },
];

const E164 = /^\+[1-9]\d{6,14}$/;

/** Whether `value` is an E.164 number (`+`, a non-zero first digit, 7 to 15 digits). */
export function isE164(value: string): boolean {
  return E164.test(value);
}

/**
 * Builds the E.164 form of a number typed in a country's national format,
 * or `undefined` if it cannot be one. Spaces, dashes, dots and brackets are
 * ignored, and a leading trunk `0` is dropped (07700 900123 → +447700900123).
 * A number typed with `+` or `00` is taken as international, whatever the
 * country.
 *
 * @param country - The selected country.
 * @param typed - What the person typed.
 */
export function toE164(country: CallingCode, typed: string): string | undefined {
  const compact = typed.replace(/[\s\-.()]/g, '');
  if (compact === '') return undefined;
  if (/^(\+|00)/.test(compact)) {
    const international = `+${compact.replace(/^(\+|00)/, '')}`;
    return isE164(international) ? international : undefined;
  }
  if (!/^\d+$/.test(compact)) return undefined;
  const national = country.code === '39' ? compact : compact.replace(/^0/, ''); // Italy keeps its 0.
  const [min, max] = country.digits;
  if (national.length < min || national.length > max) return undefined;
  const e164 = `+${country.code}${national}`;
  return isE164(e164) ? e164 : undefined;
}
